import { useState } from "react";
import { volunteerQuals } from "@/lib/quals";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, ShieldPlus, HeartPulse, Lock } from "lucide-react";

const QUALS = ["Autista", "OPEM", "TSSA", "BLSD", "TLC", "SEP", "Operatore di Sala"];

export default function AuthPage() {
  const { login, register } = useAuth();
  const { settings } = useSettings();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("volontario");
  const [quals, setQuals] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [blocked, setBlocked] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleQual = (q) =>
    setQuals((prev) => (prev.includes(q) ? prev.filter((x) => x !== q) : [...prev, q]));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setBlocked("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        setInfo(data.message);
      } else await register({ name, email, password, role, phone, qualifications: quals });
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (err.response?.status === 429) setBlocked(detail);
      else setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="relative lg:w-1/2 bg-[#D32F2F] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 0, transparent 40%)" }} />
        {/* by PZ */}
        <div className="relative px-8 lg:px-16 py-14 lg:py-24 flex flex-col h-full justify-between">
          <div className="flex items-center gap-3">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo comitato" className="h-11 w-11 rounded-xl object-cover bg-white" />
            ) : (
              <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center">
                <Plus className="h-7 w-7 text-[#D32F2F]" strokeWidth={3} />
              </div>
            )}
            <div className="leading-tight">
              <p className="font-display font-extrabold text-lg">Croce Rossa Italiana</p>
              <p className="text-white/80 text-sm">{settings.committee_name}</p>
            </div>
          </div>
          <div className="hidden lg:block max-w-md">
            <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">
              Organizza i servizi, copri ogni turno.
            </h1>
            <p className="mt-4 text-white/85 text-base leading-relaxed">
              Il coordinatore pubblica una richiesta di servizio: ambulanze, volontari e qualifiche.
              I volontari si candidano con un tocco e, a copertura raggiunta, scatta la notifica automatica.
            </p>
            <div className="mt-8 flex gap-6">
              <div className="flex items-center gap-2 text-white/90"><HeartPulse className="h-5 w-5" /><span className="text-sm">Turni in tempo reale</span></div>
              <div className="flex items-center gap-2 text-white/90"><ShieldPlus className="h-5 w-5" /><span className="text-sm">Qualifiche verificate</span></div>
            </div>
          </div>
          <div className="text-white/80">
            <p className="text-base font-bold">Gestione dei servizi</p>
          </div>
        </div>
      </div>

      {/* Form panel */}
      {/* by PZ */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-12 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <h2 className="font-display text-3xl font-bold tracking-tight">
            {mode === "login" ? "Accedi" : mode === "forgot" ? "Password dimenticata" : "Crea un account"}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {mode === "login" ? "Entra nella piattaforma del tuo comitato." : mode === "forgot" ? "Inserisci la tua email: ti invieremo un link per reimpostare la password." : "Registrati come volontario."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Nome e cognome</Label>
                <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Mario Rossi" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="nome@comitato.it" />
            </div>
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="phone">Telefono <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
                <Input id="phone" type="tel" data-testid="register-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="333 1234567" />
              </div>
            )}
            {mode !== "forgot" && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
              {mode === "login" && (
                <div className="text-right">
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setInfo(""); setBlocked(""); }} className="text-xs text-[#D32F2F] font-semibold hover:underline">
                    Password dimenticata?
                  </button>
                </div>
              )}
            </div>
            )}

            {mode === "register" && (
              <div className="space-y-2">
                <Label>Qualifiche possedute</Label>
                <div className="grid grid-cols-2 gap-2">
                  {volunteerQuals(settings.qualifications?.length ? settings.qualifications : QUALS).map((q) => (
                    <label key={q} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                      <Checkbox data-testid={`qual-${q}`} checked={quals.includes(q)} onCheckedChange={() => toggleQual(q)} />
                      <span>{q}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Ti registri come volontario. Il coordinamento è gestito dall'account del comitato.</p>
              </div>
            )}

            {blocked && (
              <div data-testid="auth-blocked" className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-amber-900">
                <Lock className="h-5 w-5 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold">Accesso temporaneamente bloccato</p>
                  <p className="mt-0.5">{blocked}</p>
                  <p className="mt-1 text-xs text-amber-800">Per troppi tentativi falliti. Attendi il tempo indicato prima di riprovare.</p>
                </div>
              </div>
            )}

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive font-medium bg-destructive/10 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            {info && (
              <p data-testid="auth-info" className="text-sm text-emerald-700 font-medium bg-emerald-50 rounded-lg px-3 py-2">
                {info}
              </p>
            )}

            <Button type="submit" data-testid="auth-submit-button" disabled={loading} className="w-full rounded-full h-11 text-base font-semibold">
              {loading ? "Attendere…" : mode === "login" ? "Accedi" : mode === "forgot" ? "Invia link di reset" : "Registrati"}
            </Button>
          </form>

          {mode === "forgot" ? (
            <p className="mt-6 text-center text-sm text-muted-foreground">
              <button data-testid="back-to-login" className="text-[#D32F2F] font-semibold hover:underline" onClick={() => { setMode("login"); setError(""); setInfo(""); setBlocked(""); }}>
                Torna all'accesso
              </button>
            </p>
          ) : (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              className="text-[#D32F2F] font-semibold hover:underline"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setInfo(""); setBlocked(""); }}
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
